'use client';

import { useState, useCallback } from 'react';
import { encodeFunctionData, type Hex } from 'viem';
import { Coins } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import { formatUSD } from '@/lib/utils';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { HV_TAKEOVER_ADAPTER_ADDRESS } from '@/lib/constants/addresses';
import { LobbyYieldPanelActionButton } from '@/features/lobby/panels/adapters/lobby-yield-panel-action';

const POKE_ALL_ABI = [{
  name: 'pokeAll',
  type: 'function',
  stateMutability: 'nonpayable',
  inputs: [
    { name: 'ugms', type: 'address[]' },
    { name: 'ugmGridIds', type: 'uint256[][]' },
    { name: 'tsfms', type: 'address[]' },
    { name: 'tsfmPoolIds', type: 'bytes32[][]' },
  ],
  outputs: [],
}] as const;

const WITHDRAW_TAX_OLD_ABI = [{
  name: 'withdrawTaxRevenue',
  type: 'function',
  stateMutability: 'nonpayable',
  inputs: [],
  outputs: [],
}] as const;

const collectTooltipSurface =
  'max-w-[260px] rounded-[6px] border border-emerald-500/28 bg-black/95 backdrop-blur-md shadow-lg px-2.5 py-2 text-[10px] leading-snug text-emerald-50/95 tracking-[0.06em]';

interface CollectButtonProps {
  pendingYieldUsd: number;
  /** Lobby boardroom allows any signer; classic `/boardroom` can pass holder-only semantics by wiring `walletConnected=false` when unstaked — here we gate on Privy wallet presence only for the flagship lobby. */
  walletConnected: boolean;
  ugms: `0x${string}`[];
  ugmGridIds: bigint[][];
  tsfms: `0x${string}`[];
  tsfmPoolIds: `0x${string}`[][];
  oldTsfms: `0x${string}`[];
  onSuccess?: () => void;
}

export function CollectButton({
  pendingYieldUsd,
  walletConnected,
  ugms,
  ugmGridIds,
  tsfms,
  tsfmPoolIds,
  oldTsfms,
  onSuccess,
}: CollectButtonProps) {
  const { executeActions } = useTransactionQueue();
  const [isPending, setIsPending] = useState(false);

  const handleCollect = useCallback(async () => {
    if (isPending) return;
    setIsPending(true);
    try {
      const withdrawSteps = oldTsfms.map((addr, i) => ({
        id: `withdraw-old-${i}`,
        label: oldTsfms.length === 1 ? 'Withdraw Tax Revenue' : `Withdraw Tax (Manager ${i + 1})`,
        description: 'Pull accumulated tax from legacy manager',
        call: {
          to: addr,
          data: encodeFunctionData({
            abi: WITHDRAW_TAX_OLD_ABI,
            functionName: 'withdrawTaxRevenue',
          }),
        },
      }));

      const pokeAllData = encodeFunctionData({
        abi: POKE_ALL_ABI,
        functionName: 'pokeAll',
        args: [ugms, ugmGridIds, tsfms, tsfmPoolIds],
      });

      await executeActions(
        'Collect Protocol Fees',
        [
          ...withdrawSteps,
          {
            id: 'poke-all',
            label: 'Collect & Flush',
            description: 'Pull remaining fees and flush yield to boardroom',
            call: {
              to: HV_TAKEOVER_ADAPTER_ADDRESS as Hex,
              data: pokeAllData,
            },
          },
        ],
        {
          onSuccess,
        },
      );
    } finally {
      setIsPending(false);
    }
  }, [isPending, executeActions, ugms, ugmGridIds, tsfms, tsfmPoolIds, oldTsfms, onSuccess]);

  const canCollect = walletConnected && pendingYieldUsd >= 1;

  const collectLabel =
    pendingYieldUsd >= 1 ? `Collect Taxes: +${formatUSD(pendingYieldUsd)}` : 'Collect Taxes';

  const button = (
    <LobbyYieldPanelActionButton
      tone="emerald"
      icon={Coins}
      label={collectLabel}
      labelClassName="normal-case tracking-[0.04em] whitespace-nowrap tabular-nums flex-1"
      disabled={isPending || !canCollect}
      pending={isPending}
      onClick={handleCollect}
    />
  );

  if (!walletConnected) {
    return (
      <Tooltip disableTouch>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex w-full">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent sideOffset={6} className={collectTooltipSurface}>
          Connect your wallet to sweep protocol taxes into the boardroom grid.
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}
